import { useEffect } from 'react';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';

/**
 * Hook that displays an error snackbar when an error occurs
 *
 * Handles various error types with improved error code support and user-friendly messages.
 * Automatically formats errors using the error formatter, which supports magic codes,
 * GraphQL extensions, and REST API error structures.
 * Shows expandable validation errors when available.
 *
 * @param error - The error to display (can be null/undefined, Error, GraphQL error, Axios error, etc.)
 * @param defaultMessage - Optional custom default message. If not provided, uses a generic error message
 * @param defaultTitle - Optional custom default title. If not provided, uses 'Error'
 *
 * @example
 * ```ts
 * // Basic usage with automatic formatting
 * useShowErrorSnackbar(queryError);
 *
 * // With custom defaults
 * useShowErrorSnackbar(error, 'Failed to load equipment data', 'Equipment Error');
 *
 * // With React Query
 * const { error } = useQuery(...);
 * useShowErrorSnackbar(error);
 * ```
 */
export const useShowErrorSnackbar = (error: unknown, defaultMessage?: string, defaultTitle?: string) => {
  const { showErrorWithDetails } = useSnackbar();

  useEffect(() => {
    if (error) {
      showErrorWithDetails(error, defaultMessage, defaultTitle);
    }
  }, [error, defaultMessage, defaultTitle, showErrorWithDetails]);
};
